import React, { useMemo } from 'react';
import Breadcrumbs from '@/components/Breadcrumbs';
import ClassCard, { ClassData } from '@/components/course/ClassCard';
import AssignmentListItem from '@/components/course/SectionCard/AssignmentListItem';
import EvaluationListItem from '@/components/course/SectionCard/EvaluationListItem';
import GlassCard from '@/components/GlassCard';
import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import { useThemeColors } from '@/hooks/useThemeColors';
import useRoutes from '@/hooks/useRoutes';
import AppLayout from '@/layouts/AppLayout';
import { AssignmentSummary, EvaluationSummary } from '@/types';
import PrimaryButton from '@/components/Buttons/PrimaryButton';
import { Link, usePage } from '@inertiajs/react';
import { ArrowLeft, BookOpen, Info, Play } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface CourseData {
    id: number;
    title: string;
    slug: string;
}

interface SectionData {
    id: number;
    title: string;
}

interface TopicCtaData {
    id: number;
    title: string;
    text?: string | null;
    link?: string | null;
    button_text?: string | null;
    order: number;
}

interface TopicData {
    id: number;
    title: string;
    order: number;
    classes: ClassData[];
    assignments?: AssignmentSummary[];
    evaluations?: EvaluationSummary[];
    ctas?: TopicCtaData[];
}

interface Props {
    course: CourseData;
    section: SectionData;
    topic: TopicData;
    is_enrolled?: boolean;
}

const TopicDetailContent: React.FC = () => {
    const { t } = useTranslation();
    const { props } = usePage<Props>();
    const { course, section, topic, is_enrolled } = props;
    const colors = useThemeColors();
    const { route } = useRoutes();
    const sortedClasses = useMemo(
        () =>
            [...topic.classes].sort(
                (a, b) =>
                    (a.order ?? Number.MAX_SAFE_INTEGER) -
                        (b.order ?? Number.MAX_SAFE_INTEGER) || a.id - b.id
            ),
        [topic.classes]
    );
    
    const breadcrumbItems = [
        { label: t('course.sectionDetail.courses'), href: route('home').toString() },
        { label: course.title, href: `/courses/${course.slug}` },
        { label: section.title, href: `/courses/${course.slug}/sections/${section.id}` },
        { label: topic.title }
    ];
    
    const handleClassClick = (classData: ClassData) => {
        // Navigate to class detail page
        window.location.href = `/courses/${course.slug}/classes/${classData.id}`;
    };
    
    return (
        <AppLayout>
            <PageTitle title={topic.title} />
            <div className="min-h-screen">
                <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
                    {/* Breadcrumbs */}
                    <div className="mb-6">
                        <Breadcrumbs items={breadcrumbItems} />
                    </div>

                    {/* Not-enrolled info banner */}
                    {!is_enrolled && (
                        <div className="mb-6 flex items-start gap-3 rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-sm">
                            <Info size={16} className="mt-0.5 shrink-0 text-yellow-600" />
                            <div>
                                <p className="font-semibold text-yellow-800">{t('course.notEnrolledBanner.heading')}</p>
                                <p className="text-yellow-700">{t('course.notEnrolledBanner.message')}</p>
                            </div>
                        </div>
                    )}

                    {/* Back to Course Link */}
                    <div className="mb-6">
                        <Link
                            href={`/courses/${course.slug}`}
                            className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors duration-200 ${colors.border} ${colors.buttonSecondary}`}
                        >
                            <ArrowLeft size={16} />
                            <span className="font-medium">{t('course.backToCourse')}</span>
                        </Link>
                    </div>
                    
                    {/* Topic Header */}
                    <GlassCard className="mb-8 shadow-lg">
                        <div className="p-6 lg:p-8">
                            <div className="flex items-center justify-between flex-wrap gap-4">
                                <div>
                                    <div className="flex items-center space-x-3 mb-3">
                                        <div className={`px-3 py-1 rounded-full text-sm font-medium ${colors.accent} ${colors.accentText}`}>
                                            {t('course.topic')} {topic.order}
                                        </div>
                                    </div>
                                    
                                    <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-2 leading-tight">
                                        {topic.title}
                                    </h1>

                                    <p className="text-gray-600 dark:text-neutral-300">
                                        {t('course.fromSection')} <span className="font-semibold">{section.title}</span> • <span className="font-semibold">{course.title}</span>
                                    </p>
                                </div>
                                
                                <div className="flex items-center space-x-4">
                                    <div className={`flex items-center space-x-2 px-4 py-2 rounded-full ${colors.accent}`}>
                                        <BookOpen className={`w-5 h-5 text-${colors.primary}`} />
                                        <span className={`font-medium ${colors.accentText}`}>
                                            {sortedClasses.length}{' '}
                                            {t('course.classes', { count: sortedClasses.length })}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </GlassCard>

                    {/* Classes Grid */}
                    <div className="space-y-6">
                        <div className="flex items-center justify-between">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                                {t('course.topicClasses')}
                            </h2>
                        </div>
                        
                        {sortedClasses.length === 0 ? (
                            <div className="bg-white dark:bg-neutral-800 rounded-xl border border-gray-200 dark:border-neutral-700 p-12 text-center">
                                <div className="w-16 h-16 bg-gray-100 dark:bg-neutral-700 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Play className="w-8 h-8 text-gray-400 dark:text-neutral-400" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                    {t('course.noClassesAvailable')}
                                </h3>
                                <p className="text-gray-600 dark:text-neutral-300 max-w-sm mx-auto">
                                    {t('course.noClassesDescription')}
                                </p>
                            </div>
                        ) : (
                            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                                {sortedClasses.map((classData, index) => (
                                    <ClassCard
                                        key={classData.id}
                                        classData={classData}
                                        classNumber={index + 1}
                                        onClick={() => handleClassClick(classData)}
                                    />
                                ))}
                            </div>
                        )}

                        {(topic.assignments || []).length > 0 && (
                            <div className="space-y-3">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                    {t('course.assignments', {
                                        count: topic.assignments?.length || 0,
                                    })}
                                </h3>
                                <div className="rounded-lg border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-3">
                                    <div className="space-y-1">
                                        {(topic.assignments || []).map(
                                            (assignment) => (
                                                <AssignmentListItem
                                                    key={`topic-assignment-${assignment.id}`}
                                                    assignment={assignment}
                                                />
                                            )
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {(topic.evaluations || []).length > 0 && (
                            <div className="space-y-3">
                                <h3 className="text-xl font-bold text-gray-900 dark:text-white capitalize">
                                    {t('course.evaluations', {
                                        count: topic.evaluations?.length || 0,
                                    })}
                                </h3>
                                <div className="rounded-lg border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-3">
                                    <div className="space-y-1">
                                        {(topic.evaluations || []).map(
                                            (evaluation) => (
                                                <EvaluationListItem
                                                    key={`topic-evaluation-${evaluation.id}`}
                                                    evaluation={evaluation}
                                                />
                                            )
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    
                    {/* CTAs */}
                    {(topic.ctas || []).length > 0 && (
                        <div className="mt-8 space-y-4">
                            {(topic.ctas || []).map((cta) => (
                                <div
                                    key={cta.id}
                                    className="bg-white dark:bg-neutral-800 rounded-xl shadow-sm border border-gray-200 dark:border-neutral-700 p-6"
                                >
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                        {cta.title}
                                    </h3>
                                    {cta.text && (
                                        <div
                                            className="mt-2 text-gray-600 dark:text-neutral-300 prose prose-sm dark:prose-invert max-w-none"
                                            dangerouslySetInnerHTML={{ __html: cta.text }}
                                        />
                                    )}
                                    {cta.link && (
                                        <div className="mt-4">
                                            <PrimaryButton as="anchor" href={cta.link} target="_blank">
                                                {cta.button_text || cta.title}
                                            </PrimaryButton>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Navigation Footer */}
                    {(() => {
                        const firstAccessibleClass = sortedClasses.find((c) => !c.coming_soon);
                        return firstAccessibleClass ? (
                            <GlassCard className="mt-8 p-6 shadow-lg">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 mb-2 dark:text-white">
                                            {t('course.readyToStart')}
                                        </h3>
                                        <p className="text-gray-600 dark:text-neutral-300">
                                            {t('course.startFirstClass')}
                                        </p>
                                    </div>

                                    <button
                                        onClick={() => handleClassClick(firstAccessibleClass)}
                                        className="btn-gradient flex items-center space-x-2 rounded-lg px-6 py-3 font-semibold"
                                    >
                                        <Play size={18} fill="currentColor" />
                                        <span>{t('course.start')}</span>
                                    </button>
                                </div>
                            </GlassCard>
                        ) : null;
                    })()}
                </div>
            </div>
        </AppLayout>
    );
};

const TopicDetail: React.FC<Props> = ({ course, section, topic }) => {
    return (
        <ThemeWrapper>
            <TopicDetailContent />
        </ThemeWrapper>
    );
};

export default TopicDetail;
